import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { FaBoxOpen, FaEuroSign, FaExclamationTriangle, FaMoneyBillWave } from 'react-icons/fa';
import useExchangeRate from '../../hooks/useExchangeRate';

const getMonthRange = () => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    start: first.toISOString().slice(0, 10),
    end: last.toISOString().slice(0, 10)
  };
};

const Dashboard = () => {
  const [stats, setStats] = useState({ orders: 0, revenue: 0, lowStock: 0 });
  const [loading, setLoading] = useState(true);
  const [salesData, setSalesData] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [salesLoading, setSalesLoading] = useState(false);
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('userInfo');
    return stored ? JSON.parse(stored) : null;
  });
  const exchangeRate = useExchangeRate();
  const [rateModal, setRateModal] = useState(false);
  const [newRate, setNewRate] = useState('');
  const [rateError, setRateError] = useState('');
  const [rateLoading, setRateLoading] = useState(false);

  // Set default date range to current month
  useEffect(() => {
    const { start, end } = getMonthRange();
    setStartDate(start);
    setEndDate(end);
  }, []);

  useEffect(() => {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
    const token = localStorage.getItem('jwtToken');
    const headers = { Authorization: `Bearer ${token}` };
    const fetchStats = async () => {
      setLoading(true);
      try {
        const [ordersRes, revenueRes, lowStockRes] = await Promise.all([
          fetch(`${apiBaseUrl}/api/admin/stats/orders`, { headers }),
          fetch(`${apiBaseUrl}/api/admin/stats/revenue`, { headers }),
          fetch(`${apiBaseUrl}/api/admin/stats/low-stock`, { headers }),
        ]);
        const orders = await ordersRes.json();
        const revenue = await revenueRes.json();
        const lowStock = await lowStockRes.json();
        setStats({
          orders: orders.count || 0,
          revenue: revenue.revenue || 0,
          lowStock: lowStock.lowStock || 0,
        });
      } catch (err) {
        setStats({ orders: 0, revenue: 0, lowStock: 0 });
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  // Fetch sales data (with date filtering)
  useEffect(() => {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
    const token = localStorage.getItem('jwtToken');
    const headers = { Authorization: `Bearer ${token}` };
    const fetchSales = async () => {
      setSalesLoading(true);
      try {
        let url = `${apiBaseUrl}/api/admin/stats/sales-over-time`;
        const params = [];
        if (startDate) params.push(`start=${startDate}`);
        if (endDate) params.push(`end=${endDate}`);
        if (params.length) url += `?${params.join('&')}`;
        const res = await fetch(url, { headers });
        const sales = await res.json();
        setSalesData((sales.sales || []).map(d => ({ date: d.date, sales: d.total })));
      } catch (err) {
        setSalesData([]);
      } finally {
        setSalesLoading(false);
      }
    };
    fetchSales();
  }, [startDate, endDate]);

  const handleOpenRateModal = () => {
    setNewRate(exchangeRate ? String(exchangeRate) : '');
    setRateError('');
    setRateModal(true);
  };
  const handleCloseRateModal = () => {
    setRateModal(false);
    setRateError('');
  };
  const handleUpdateRate = async () => {
    setRateLoading(true);
    setRateError('');
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
      const token = localStorage.getItem('jwtToken');
      const res = await fetch(`${apiBaseUrl}/api/admin/exchange-rate`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ value: parseFloat(newRate) })
      });
      if (!res.ok) throw new Error('Failed to update exchange rate');
      setRateModal(false);
      window.location.reload(); // reload to refresh all usages
    } catch (err) {
      setRateError(err.message || 'Failed to update exchange rate');
    } finally {
      setRateLoading(false);
    }
  };

  return (
    <div className="w-full max-w-full p-2 md:p-8">
      <h1 className="text-lg md:text-3xl font-bold mb-4 md:mb-6 text-blue-900 break-words">Welcome, {user?.email || 'Admin'}!</h1>
      {/* Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 md:gap-4 mb-4 md:mb-8 w-full max-w-full">
        <div className="bg-gradient-to-br from-blue-500 to-blue-300 text-white rounded-xl shadow-lg p-4 md:p-6 flex flex-col items-center justify-center relative overflow-hidden w-full max-w-xs mx-auto">
          <FaBoxOpen className="text-2xl md:text-4xl mb-2 drop-shadow-lg" />
          <div className="text-xl md:text-3xl font-extrabold">{loading ? '...' : stats.orders}</div>
          <div className="text-blue-100 mt-2 font-semibold text-xs md:text-base">Orders</div>
        </div>
        <div className="bg-gradient-to-br from-green-400 to-green-200 text-white rounded-xl shadow-lg p-4 md:p-6 flex flex-col items-center justify-center relative overflow-hidden w-full max-w-xs mx-auto">
          <FaEuroSign className="text-2xl md:text-4xl mb-2 drop-shadow-lg" />
          <div className="text-xl md:text-3xl font-extrabold">{loading ? '...' : `€${stats.revenue}`}</div>
          <div className="text-green-100 mt-2 font-semibold text-xs md:text-base">Revenue</div>
        </div>
        <div className="bg-gradient-to-br from-yellow-400 to-yellow-200 text-white rounded-xl shadow-lg p-4 md:p-6 flex flex-col items-center justify-center relative overflow-hidden w-full max-w-xs mx-auto">
          <FaExclamationTriangle className="text-2xl md:text-4xl mb-2 drop-shadow-lg" />
          <div className="text-xl md:text-3xl font-extrabold">{loading ? '...' : stats.lowStock}</div>
          <div className="text-yellow-100 mt-2 font-semibold text-xs md:text-base">Low Stock</div>
        </div>
        {/* TND Exchange Rate Card */}
        <button
          className="bg-gradient-to-br from-gray-100 to-green-100 text-green-900 rounded-xl shadow-lg p-4 md:p-6 flex flex-col items-center justify-center relative overflow-hidden w-full max-w-xs mx-auto border border-green-200 hover:shadow-xl transition"
          onClick={handleOpenRateModal}
        >
          <FaMoneyBillWave className="text-2xl md:text-4xl mb-2 drop-shadow-lg text-green-700" />
          <div className="text-xl md:text-2xl font-extrabold">{exchangeRate ? `1 EUR = ${exchangeRate} TND` : '...'}</div>
          <div className="text-green-900 mt-2 font-semibold text-xs md:text-base">TND Exchange Rate</div>
        </button>
      </div>
      {/* Modal for updating exchange rate */}
      {rateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md relative">
            <button onClick={handleCloseRateModal} className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-2xl">&times;</button>
            <h3 className="text-xl font-bold mb-4 text-green-700">Update TND Exchange Rate</h3>
            <div className="mb-2">Current: <span className="font-bold">1 EUR = {exchangeRate} TND</span></div>
            <div className="mb-2">New Value:</div>
            <input
              type="number"
              step="0.0001"
              min="0"
              className="border rounded px-3 py-2 w-full mb-2"
              value={newRate}
              onChange={e => setNewRate(e.target.value)}
              placeholder="New exchange rate"
            />
            <div className="mb-4">Preview: <span className="font-bold">1 EUR = {newRate || exchangeRate} TND</span></div>
            {rateError && <div className="text-red-500 text-sm mb-2">{rateError}</div>}
            <button
              onClick={handleUpdateRate}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded shadow w-full"
              disabled={!newRate || isNaN(parseFloat(newRate)) || parseFloat(newRate) <= 0 || rateLoading}
            >
              {rateLoading ? 'Updating...' : 'Update'}
            </button>
          </div>
        </div>
      )}
      {/* Sales Chart with Date Filtering */}
      <div className="bg-white rounded-xl shadow-lg p-2 md:p-6 flex flex-col w-full max-w-full min-h-[200px]">
        <div className="flex flex-col md:flex-col lg:flex-row md:gap-4 mb-4 w-full">
          <h2 className="text-base md:text-xl font-semibold mb-2 md:mb-0 text-blue-900">Sales Over Time</h2>
          <div className="flex flex-col sm:flex-row gap-2 items-center w-full md:w-auto">
            <label className="text-xs md:text-sm w-full sm:w-auto">Start:</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="border rounded px-2 py-1 w-full sm:w-auto" />
            <label className="text-xs md:text-sm w-full sm:w-auto">End:</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="border rounded px-2 py-1 w-full sm:w-auto" />
          </div>
        </div>
        <div className="w-full h-full min-h-[150px] md:min-h-[200px] flex-1 flex items-center justify-center overflow-x-auto">
          {salesLoading ? (
            <div className="text-center text-gray-400">Loading sales data...</div>
          ) : salesData && salesData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250} minWidth={300} minHeight={200}>
              <LineChart data={salesData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="sales" stroke="#8884d8" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center text-gray-400">No sales data for selected range.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 